import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export interface Comment {
  id: string;
  planId: string;
  createdByMemberId: string | null;
  createdByAgentId: string | null;
  createdByType: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind: string | null;
  createdByName: string | null;
  /** 작성에 사용된 도구(에이전트 설정) 이름. 행위 주체 축과 직교한다. */
  createdByAgentName: string | null;
  updatedByMemberId?: string | null;
  updatedByAgentId?: string | null;
  updatedByCredentialKind?: string | null;
  updatedByName?: string | null;
  updatedByAgentName?: string | null;
  type: string;
  content: string;
  affectedFiles: string[];
  // 답글 개수(soft-delete 제외). 펼치기 전에도 답글 토글 노출/뱃지를 판단하기 위해 원댓글에 포함된다.
  replyCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

type CommentListResponse = {
  data: Comment[];
  meta: PaginationMeta;
};

type CommentResponse = {
  data: Comment;
};

/**
 * 원댓글 단건 표면(`/api/projects/:projectId/comments/:id`)의 URL.
 * 서버가 targetType과 무관하게 이 경로 하나로 수정/삭제를 처리하므로
 * 플랜 본문·태스크·finding 코멘트가 모두 이 빌더를 공유한다.
 */
export const buildRootCommentUrl = (projectId: string, commentId: string) =>
  `${getApiUrl()}/api/projects/${projectId}/comments/${commentId}`;

export const fetchComments = async (
  token: string,
  projectId: string,
  planId: string,
  params?: {
    type?: string;
    order?: 'asc' | 'desc';
  },
): Promise<Comment[]> => {
  const queryParams = new URLSearchParams();
  if (params?.type) queryParams.set('type', params.type);
  if (params?.order) queryParams.set('order', params.order);

  const url = `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/comments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch comments: ${response.status}`);
  }

  const data = (await response.json()) as CommentListResponse;
  return data.data;
};

export const fetchCommentsPaged = async (
  token: string,
  projectId: string,
  planId: string,
  params?: {
    type?: string;
    order?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
  },
): Promise<PagedData<Comment>> => {
  const queryParams = new URLSearchParams();
  if (params?.type) queryParams.set('type', params.type);
  if (params?.order) queryParams.set('order', params.order);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/plans/${planId}/comments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch comments: ${response.status}`);
  }

  return (await response.json()) as PagedData<Comment>;
};

export const fetchCommentById = async (token: string, projectId: string, id: string): Promise<Comment> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/comments/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch comment: ${response.status}`);
  }

  const data = (await response.json()) as CommentResponse;
  return data.data;
};

export const createComment = async (
  token: string,
  projectId: string,
  input: {
    planId: string;
    type: string;
    content: string;
    affectedFiles?: string[];
  },
): Promise<Comment> => {
  const body = {
    type: input.type,
    content: input.content,
    affectedFiles: input.affectedFiles,
  };

  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/plans/${input.planId}/comments`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create comment: ${response.status}`);
  }

  const data = (await response.json()) as CommentResponse;
  return data.data;
};

export const updateComment = async (
  token: string,
  projectId: string,
  id: string,
  input: Partial<Pick<Comment, 'content' | 'type' | 'affectedFiles'>>,
): Promise<Comment> => {
  const response = await fetchWithAuthRetry(buildRootCommentUrl(projectId, id), {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update comment: ${response.status}`);
  }

  const data = (await response.json()) as CommentResponse;
  return data.data;
};

export const deleteComment = async (token: string, projectId: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(buildRootCommentUrl(projectId, id), {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete comment: ${response.status}`);
  }
};
